import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export interface CrmChatPanelLayout {
  isOpen: boolean;
  width: number;
  isMobile: boolean;
}

interface CrmChatLayoutContextValue {
  /** Pixels to reserve on the right of the main column (desktop docked panel only). */
  reservedWidth: number;
  setPanelLayout: (layout: CrmChatPanelLayout) => void;
}

const CrmChatLayoutContext = createContext<CrmChatLayoutContextValue | null>(null);

function computeReservedWidth(layout: CrmChatPanelLayout): number {
  return layout.isOpen && !layout.isMobile && layout.width > 0 ? layout.width : 0;
}

export const CrmChatLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reservedWidth, setReservedWidth] = useState(0);

  const setPanelLayout = useCallback((layout: CrmChatPanelLayout) => {
    setReservedWidth(computeReservedWidth(layout));
  }, []);

  const value = useMemo(
    () => ({ reservedWidth, setPanelLayout }),
    [reservedWidth, setPanelLayout]
  );

  return <CrmChatLayoutContext.Provider value={value}>{children}</CrmChatLayoutContext.Provider>;
};

/** Right inset (px) for the main app column when Ask Type+ is open on desktop. */
export function useCrmChatLayoutInset(): number {
  return useContext(CrmChatLayoutContext)?.reservedWidth ?? 0;
}

export function useCrmChatPanelLayoutReporter(): CrmChatLayoutContextValue["setPanelLayout"] {
  const ctx = useContext(CrmChatLayoutContext);
  if (!ctx) {
    return () => {
      // drawer may render outside provider in tests
    };
  }
  return ctx.setPanelLayout;
}
